import { Controller } from '@hotwired/stimulus'
import {
  createLoader,
  createHighlight,
  createPopover,
  createSearchPager,
  createSearchResultsPanel
} from '../../services/progress_panel'

export default class extends Controller {
  static targets = ['trigger', 'label', 'popover', 'search', 'list', 'empty', 'loading', 'error', 'hidden', 'rowTemplate']
  static values = { searchUrl: String, debounce: { type: Number, default: 300 }, linkUrlBase: String }

  declare readonly triggerTarget: HTMLButtonElement
  declare readonly labelTarget: HTMLElement
  declare readonly popoverTarget: HTMLElement
  declare readonly searchTarget: HTMLInputElement
  declare readonly listTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly loadingTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly hiddenTarget: HTMLInputElement
  declare readonly rowTemplateTarget: HTMLTemplateElement

  declare readonly hasLabelTarget: boolean
  declare readonly hasSearchTarget: boolean
  declare readonly hasListTarget: boolean
  declare readonly hasEmptyTarget: boolean
  declare readonly hasLoadingTarget: boolean
  declare readonly hasErrorTarget: boolean
  declare readonly hasHiddenTarget: boolean
  declare readonly hasRowTemplateTarget: boolean

  declare readonly searchUrlValue: string
  declare readonly debounceValue: number
  declare readonly linkUrlBaseValue: string

  private timer: number | undefined
  private loader!: ReturnType<typeof createLoader>
  private highlight!: ReturnType<typeof createHighlight>
  private popover!: ReturnType<typeof createPopover>

  private get query(): string {
    return this.hasSearchTarget ? this.searchTarget.value.trim() : ''
  }

  private get search(): HTMLElement | null {
    return this.hasSearchTarget ? this.searchTarget : null
  }

  connect() {
    this.popover = createPopover({
      trigger: this.triggerTarget,
      popover: this.popoverTarget,
      search: this.search
    })

    this.highlight = createHighlight({ list: this.listTarget, search: this.search }, row => this.select(row))

    this.loader = createLoader({
      pager: createSearchPager(this.searchUrlValue),
      panel: createSearchResultsPanel({
        list: this.hasListTarget ? this.listTarget : undefined,
        loading: this.hasLoadingTarget ? this.loadingTarget : undefined,
        error: this.hasErrorTarget ? this.errorTarget : undefined,
        empty: this.hasEmptyTarget ? this.emptyTarget : undefined,
        rowTemplate: this.hasRowTemplateTarget ? this.rowTemplateTarget : null
      }),
      query: () => this.query,
      linkUrlBase: this.linkUrlBaseValue,
      loading: this.hasLoadingTarget ? this.loadingTarget : undefined,
      onResults: () => this.highlight.reset()
    })
  }

  toggle() {
    if (this.popover.isOpen()) this.close()
    else this.open()
  }

  open() {
    if (this.popover.open()) {
      this.highlight.reset()

      void this.loader.load()
    }
  }

  close() {
    this.loader.cancel()
    window.clearTimeout(this.timer)
    this.popover.close()
    this.highlight.reset()
  }

  onSearch() {
    window.clearTimeout(this.timer)

    this.timer = window.setTimeout(() => { void this.loader.load() }, this.debounceValue)
  }

  triggerKeydown(event: KeyboardEvent) {
    if (event.key !== 'ArrowDown') return

    event.preventDefault()
    this.open()
  }

  private keyActions: Record<string, (event: KeyboardEvent) => void> = {
    ArrowDown: event => {
      event.preventDefault()
      this.highlight.move(1)
    },
    ArrowUp: event => {
      event.preventDefault()
      this.highlight.move(-1)
    },
    Enter: event => {
      event.preventDefault()
      this.highlight.pick()
    },
    Escape: event => {
      event.preventDefault()
      this.close()
      this.triggerTarget.focus()
    },
    Tab: () => this.close()
  }

  onKeydown(event: KeyboardEvent) {
    this.keyActions[event.key]?.(event)
  }

  onPick(event: Event) {
    const row = (event.target as HTMLElement).closest('[role="option"]') as HTMLElement | null
    if (!row) return

    if (row.dataset.type === 'nested') return

    this.select(row)
  }

  onListScroll() {
    const list = this.listTarget

    if (list.scrollHeight - list.scrollTop - list.clientHeight > 120) return

    void this.loader.loadMore()
  }

  private select(row: HTMLElement) {
    if (this.hasHiddenTarget) {
      this.hiddenTarget.value = row.dataset.id ?? ''
      this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }

    if (this.hasLabelTarget) this.labelTarget.textContent = row.dataset.label ?? ''

    this.close()
    this.triggerTarget.focus()
  }
}
